import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  RadioGroup, 
  Radio, 
  FormControlLabel, 
  Button, 
  Paper, 
  Grid,
  FormControl,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Tooltip
} from '@mui/material';
import { API_BASE_URL } from '../config/api';

const BACKEND_URL = API_BASE_URL;

// Question status constants
const STATUS = {
  NOT_VISITED: 'not_visited',
  NOT_ANSWERED: 'not_answered',
  ANSWERED: 'answered',
  MARKED_FOR_REVIEW: 'marked_for_review',
  ANSWERED_MARKED_FOR_REVIEW: 'answered_marked_for_review'
};

// Track which questions have been visited
let visitedQuestions = new Set();

const ExamQuestions = forwardRef(({ examId: propExamId, onSubmit, onReturnToStart, timeRemaining, formatTime, ...props }, ref) => {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  // Always start with Question 1 (index 0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [examId, setExamId] = useState(null);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Status tracking for each question
  const [questionStatus, setQuestionStatus] = useState({});
  // Track visited questions
  const [visited, setVisited] = useState(new Set([0])); // Start with Question 1 (index 0) as visited

  // Create a ref to expose methods to parent components
  const methodsRef = useRef({});

  // Expose methods to parent component through ref
  useImperativeHandle(ref, () => ({
    submitExam: handleSubmitExam, // Use handleSubmitExam to show the confirmation dialog
    autoSubmitExam: confirmSubmit, // Direct submission without confirmation dialog for auto-submit
    getExamData: () => ({
      examId,
      answers,
      questions
    }),
    resetExam: resetExamState
  }));

  // Update examId state when prop changes
  useEffect(() => {
    if (propExamId) {
      setExamId(propExamId);
      console.log('ExamQuestions - Setting examId from prop:', propExamId);
    }
  }, [propExamId]);

  // Fetch questions when component mounts or examId changes
  useEffect(() => {
    // Reset everything and fetch questions
    resetExamState();
    fetchQuestions();
  }, [propExamId]); // Re-fetch when propExamId changes
  
  // Function to reset exam state
  const resetExamState = () => {
    // Reset current question to 1
    setCurrentQuestionIndex(0);
    // Clear answers
    setAnswers({});
    // Clear question status
    setQuestionStatus({});
    // Reset visited questions - only Question 1 (index 0) is visited initially
    setVisited(new Set([0]));
    visitedQuestions = new Set([0]);
    // Clear localStorage items from previous exams
    localStorage.removeItem('examId');
    localStorage.removeItem('examAnswers');
    localStorage.removeItem('examQuestions');
    localStorage.removeItem('visitedQuestions');
  };

  // Store exam data in localStorage when it changes
  useEffect(() => {
    if (examId) {
      localStorage.setItem('examId', examId);
    }
  }, [examId]);
  
  // Store answers in localStorage whenever they change
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem('examAnswers', JSON.stringify(answers));
    }
  }, [answers]);

  // Initialize question status when questions are loaded
  useEffect(() => {
    if (questions.length > 0) {
      const initialStatus = {};
      questions.forEach((q, index) => {
        // ALL questions start as NOT_VISITED initially
        // Only Question 1 is set to NOT_ANSWERED because it's the first question shown
        initialStatus[q._id] = index === 0 ? STATUS.NOT_ANSWERED : STATUS.NOT_VISITED;
      });
      setQuestionStatus(initialStatus);
      
      // Reset visited questions - only Question 1 (index 0) is visited initially
      setVisited(new Set([0]));
      visitedQuestions = new Set([0]);
      
      // Ensure we're starting at Question 1
      setCurrentQuestionIndex(0);
    }
  }, [questions]);

  // Update question status when answers change
  useEffect(() => {
    if (Object.keys(answers).length > 0 && Object.keys(questionStatus).length > 0) {
      setQuestionStatus(prevStatus => {
        const updatedStatus = { ...prevStatus };
        let hasChanges = false;

        Object.keys(answers).forEach(qId => {
          // If question is answered
          if (answers[qId]) {
            // Check if it's marked for review
            if (updatedStatus[qId] === STATUS.MARKED_FOR_REVIEW) {
              updatedStatus[qId] = STATUS.ANSWERED_MARKED_FOR_REVIEW;
              hasChanges = true;
            } else if (updatedStatus[qId] !== STATUS.ANSWERED_MARKED_FOR_REVIEW && updatedStatus[qId] !== STATUS.ANSWERED) {
              updatedStatus[qId] = STATUS.ANSWERED;
              hasChanges = true;
            }
          }
          // If question is not answered but has been visited
          else if (updatedStatus[qId] !== STATUS.NOT_VISITED) {
            if (updatedStatus[qId] === STATUS.ANSWERED_MARKED_FOR_REVIEW) {
              updatedStatus[qId] = STATUS.MARKED_FOR_REVIEW;
              hasChanges = true;
            } else if (updatedStatus[qId] === STATUS.ANSWERED) {
              updatedStatus[qId] = STATUS.NOT_ANSWERED;
              hasChanges = true;
            }
          }
        });

        return hasChanges ? updatedStatus : prevStatus;
      });
    }
  }, [answers, questions]);

  const fetchQuestions = async () => {
    setLoading(true);
    setError(null);

    try {
      // MUST use the persistent exam ID from props first, then localStorage
      const persistentExamId = propExamId || examId || localStorage.getItem('examId');
      const userId = localStorage.getItem('userId');

      console.log('ExamQuestions fetchQuestions - propExamId:', propExamId, 'examId state:', examId, 'localStorage examId:', localStorage.getItem('examId'));

      if (!persistentExamId) {
        throw new Error('No exam ID available');
      }

      // Using fetch to start exam with persistent ID
      const response = await fetch(`${BACKEND_URL}/api/exam/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          num_questions: 50,
          exam_id: persistentExamId, // Use persistent exam ID
          user_id: userId
        }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setQuestions(data.questions);
        // ALWAYS use the persistent exam ID, never generate new ones
        const finalExamId = persistentExamId; // Use the persistent ID we validated earlier
        setExamId(finalExamId);

        console.log('Using persistent exam ID for questions:', finalExamId);

        // Initialize answers object with empty values
        const initialAnswers = {};
        data.questions.forEach(q => {
          initialAnswers[q._id] = '';
        });
        setAnswers(initialAnswers);

        // Send initial empty answers to backend
        try {
          import('../socket').then(({ default: socket }) => {
            if (socket && socket.connected) {
              socket.emit('update_answers', {
                answers: initialAnswers,
                exam_id: examId,
                timestamp: new Date().toISOString()
              });
            }
          });
        } catch (error) {
          console.error('Error sending initial answers to backend:', error);
        }

        // Reset question status to ensure all questions start as NOT_VISITED
        // except for Question 1
        const initialStatus = {};
        data.questions.forEach((q, index) => {
          initialStatus[q._id] = index === 0 ? STATUS.NOT_ANSWERED : STATUS.NOT_VISITED;
        });
        setQuestionStatus(initialStatus);

        // Ensure we start at Question 1
        setCurrentQuestionIndex(0);
      } else {
        setError('Failed to start exam: ' + data.message);
      }
    } catch (err) {
      console.error('Error starting exam:', err);
      setError(`Error starting exam: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    const updatedAnswers = {
      ...answers,
      [questionId]: value
    };

    setAnswers(updatedAnswers);

    // Send real-time answer updates to backend for termination scenarios
    try {
      // Import socket from the shared socket instance
      import('../socket').then(({ default: socket }) => {
        if (socket && socket.connected) {
          socket.emit('update_answers', {
            answers: updatedAnswers,
            exam_id: examId,
            timestamp: new Date().toISOString()
          });
        }
      });
    } catch (error) {
      console.error('Error sending answer update to backend:', error);
    }
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Get the next question index
      const nextIndex = currentQuestionIndex + 1;

      // Mark this question as visited
      const newVisited = new Set(visited);
      newVisited.add(nextIndex);
      setVisited(newVisited);
      visitedQuestions.add(nextIndex);

      // Update status of next question if it hasn't been visited using functional update
      const nextQuestionId = questions[nextIndex]._id;
      setQuestionStatus(prevStatus => {
        if (prevStatus[nextQuestionId] === STATUS.NOT_VISITED) {
          return {
            ...prevStatus,
            [nextQuestionId]: STATUS.NOT_ANSWERED
          };
        }
        return prevStatus; // No change needed
      });

      // Move to next question
      setCurrentQuestionIndex(nextIndex);
      console.log(`🔄 Moved to question ${nextIndex + 1}`);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitExam = () => {
    setDialogOpen(true);
  };

  const confirmSubmit = async () => {
    if (dialogOpen) {
      setDialogOpen(false);
    }
    setLoading(true);
    
    try {
      // Calculate the score
      let correctCount = 0;
      questions.forEach(question => {
        if (answers[question._id] === question.correctAnswer) {
          correctCount++;
        }
      });
      
      const score = (correctCount / questions.length) * 100;
      
      // Get user ID from localStorage or use a default
      const userId = localStorage.getItem('userId') || 'unknown';
      
      // Get warning count if available
      const warningCount = parseInt(localStorage.getItem('warningCount') || '0');
      
      // Create questions_with_answers array in the format you specified
      const questions_with_answers = questions.map(question => ({
        question_id: question._id,
        questionText: question.questionText,
        options: question.options,
        correctAnswer: question.correctAnswer,
        student_answer: answers[question._id] || ""
      }));

      // Don't submit directly to API - let parent handle submission to prevent duplicates
      console.log('Preparing exam submission data for parent component...');

      // Calculate statistics for the summary
      const attemptedCount = Object.values(answers).filter(a => a !== '').length;
      const notAttemptedCount = questions.length - attemptedCount;

      // Store questions in localStorage for the App component to use
      localStorage.setItem('examQuestions', JSON.stringify(questions));

      // Set exam as submitted to show the success message
      setExamSubmitted(true);

      // Call parent's onSubmit handler with submission data - parent will handle API call
      if (onSubmit && typeof onSubmit === 'function') {
        console.log('Calling parent onSubmit handler to prevent double submission...');

        // Include termination data if available (passed from parent)
        const submissionData = {
          exam_id: propExamId || examId, // Use prop examId first
          user_id: userId,
          score: score,
          correct_answers: correctCount,
          total_questions: questions.length,
          answers: answers,
          questions_with_answers: questions_with_answers,
          warning_count: warningCount
        };

        // Add termination data if this is a terminated exam
        if (props.isTerminated) {
          submissionData.status = 'terminated';
          submissionData.termination_reason = 'Maximum warnings reached';
          submissionData.terminated_at = new Date().toISOString();
        }

        onSubmit(submissionData);
      } else {
        console.error('No onSubmit handler provided by parent component');
        setError('Submission handler not available. Please contact support.');
      }
    } catch (err) {
      console.error('Error submitting exam:', err);
      setError(`Error submitting exam: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const navigateToQuestion = (index) => {
    // Mark this question as visited
    const newVisited = new Set(visited);
    newVisited.add(index);
    setVisited(newVisited);
    visitedQuestions.add(index);

    const questionId = questions[index]._id;
    // Update status if not visited before using functional update
    setQuestionStatus(prevStatus => {
      if (prevStatus[questionId] === STATUS.NOT_VISITED) {
        return {
          ...prevStatus,
          [questionId]: STATUS.NOT_ANSWERED
        };
      }
      return prevStatus; // No change needed
    });

    // Set as current question
    setCurrentQuestionIndex(index);
  };

  const handleSaveAndNext = () => {
    // Current question is considered answered if an answer exists
    const currentQuestionId = questions[currentQuestionIndex]._id;
    if (answers[currentQuestionId]) {
      setQuestionStatus(prevStatus => ({
        ...prevStatus,
        [currentQuestionId]: STATUS.ANSWERED
      }));
    }
    goToNextQuestion();
  };

  const handleClear = () => {
    const currentQuestionId = questions[currentQuestionIndex]._id;
    const updatedAnswers = {
      ...answers,
      [currentQuestionId]: ''
    };

    setAnswers(updatedAnswers);

    // Send real-time answer updates to backend for termination scenarios
    try {
      // Import socket from the shared socket instance
      import('../socket').then(({ default: socket }) => {
        if (socket && socket.connected) {
          socket.emit('update_answers', {
            answers: updatedAnswers,
            exam_id: examId,
            timestamp: new Date().toISOString()
          });
        }
      });
    } catch (error) {
      console.error('Error sending answer update to backend:', error);
    }
  };

  const handleMarkForReview = () => {
    console.log('Mark for Review button clicked');

    if (!questions[currentQuestionIndex]) {
      console.error('No current question found');
      return;
    }

    const currentQuestionId = questions[currentQuestionIndex]._id;
    const currentStatus = questionStatus[currentQuestionId];

    console.log('Current question ID:', currentQuestionId);
    console.log('Current status:', currentStatus);
    console.log('Current answer:', answers[currentQuestionId]);

    // Toggle between marked for review and its previous state (answered/not answered)
    let newStatus;
    if (currentStatus === STATUS.MARKED_FOR_REVIEW) {
        newStatus = answers[currentQuestionId] ? STATUS.ANSWERED : STATUS.NOT_ANSWERED;
    } else if (currentStatus === STATUS.ANSWERED_MARKED_FOR_REVIEW) {
        newStatus = answers[currentQuestionId] ? STATUS.ANSWERED : STATUS.NOT_ANSWERED; // Should not happen if answer exists
    } else {
        newStatus = answers[currentQuestionId] ? STATUS.ANSWERED_MARKED_FOR_REVIEW : STATUS.MARKED_FOR_REVIEW;
    }

    console.log('New status will be:', newStatus);

    // Update the status immediately
    setQuestionStatus(prevStatus => {
      const updatedStatus = {
        ...prevStatus,
        [currentQuestionId]: newStatus
      };
      console.log('✅ Status updated for question', currentQuestionIndex + 1, ':', newStatus);
      console.log('✅ Full updated status:', updatedStatus);
      return updatedStatus;
    });

    // Navigate to next question with a small delay to ensure state update
    setTimeout(() => {
      console.log('🔄 Navigating to next question...');
      goToNextQuestion();
    }, 100);
  };

  // Function to get button color based on question status
  const getStatusColor = (status) => {
    switch(status) {
      case STATUS.NOT_VISITED:
        return { bgcolor: '#e0e0e0', color: '#000' }; // Gray
      case STATUS.NOT_ANSWERED:
        return { bgcolor: '#ff5252', color: '#fff' }; // Red
      case STATUS.ANSWERED:
        return { bgcolor: '#4caf50', color: '#fff' }; // Green
      case STATUS.MARKED_FOR_REVIEW:
        return { bgcolor: '#673ab7', color: '#fff' }; // Purple
      case STATUS.ANSWERED_MARKED_FOR_REVIEW:
        return { bgcolor: '#2196f3', color: '#fff' }; // Blue
      default:
        return { bgcolor: '#e0e0e0', color: '#000' };
    }
  };

  const renderCurrentQuestion = () => {
    if (!questions.length) return null;
    
    const question = questions[currentQuestionIndex];
    
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Question {currentQuestionIndex + 1} of {questions.length}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          {question.questionText}
        </Typography>
        
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={answers[question._id] || ''}
            onChange={(e) => handleAnswerChange(question._id, e.target.value)}
          >
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Navigation buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleSaveAndNext}
            sx={{ mr: 1 }}
          >
            SAVE & NEXT
          </Button>
          
          <Button 
            variant="outlined"
            onClick={handleClear}
            sx={{ mr: 1 }}
          >
            CLEAR
          </Button>
          
          <Button
            variant="contained"
            color="secondary"
            onClick={handleMarkForReview}
          >
            {(() => {
              const currentQuestionId = questions[currentQuestionIndex]?._id;
              const currentStatus = questionStatus[currentQuestionId];

              if (currentStatus === STATUS.MARKED_FOR_REVIEW || currentStatus === STATUS.ANSWERED_MARKED_FOR_REVIEW) {
                return "UNMARK REVIEW";
              }
              return "MARK FOR REVIEW";
            })()}
          </Button>
        </Box>
      </Paper>
    );
  };

  const renderQuestionLegend = () => {
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 3, border: '1px dashed #ccc' }}>
        <Typography variant="h6" gutterBottom>
          Question Status Legend
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#e0e0e0', mr: 1 }}></Box>
            <Typography variant="body2">Not Visited</Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#ff5252', mr: 1 }}></Box>
            <Typography variant="body2">Not Answered</Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#4caf50', mr: 1 }}></Box>
            <Typography variant="body2">Answered</Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#673ab7', color: '#fff', mr: 1 }}></Box>
            <Typography variant="body2">Marked for Review</Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#2196f3', color: '#fff', mr: 1 }}></Box>
            <Typography variant="body2">Answered & Marked for Review</Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#ff9800', color: '#fff', mr: 1 }}></Box>
            <Typography variant="body2">Current Question</Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderQuestionNavigation = () => {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Question Navigation
        </Typography>
        <Grid container spacing={1}>
          {questions.map((question, index) => {
            // Current question always takes precedence over any status
            const isCurrentQuestion = index === currentQuestionIndex;
            
            if (isCurrentQuestion) {
              // Current question is always orange, regardless of its status
              return (
                <Grid item key={index}>
                  <Tooltip title={`Question ${index + 1} (Current)`}>
                    <Button
                      variant="contained"
                      sx={{
                        minWidth: '36px',
                        height: '36px',
                        p: 0,
                        bgcolor: '#ff9800', // Orange
                        color: '#fff',
                        '&:hover': {
                          bgcolor: '#f57c00', // Darker orange on hover
                          opacity: 0.9
                        }
                      }}
                      onClick={() => navigateToQuestion(index)}
                    >
                      {index + 1}
                    </Button>
                  </Tooltip>
                </Grid>
              );
            } else {
              // For non-current questions, check if it's been visited
              const hasBeenVisited = visited.has(index);
              
              // If it hasn't been visited, always show as NOT_VISITED (gray)
              // regardless of what's in questionStatus
              let bgcolor, color;
              
              if (!hasBeenVisited) {
                // Not visited - always gray
                const notVisitedColors = getStatusColor(STATUS.NOT_VISITED);
                bgcolor = notVisitedColors.bgcolor;
                color = notVisitedColors.color;
              } else {
                // Has been visited - use status color
                const status = questionStatus[question._id] || STATUS.NOT_ANSWERED;
                const statusColors = getStatusColor(status);
                bgcolor = statusColors.bgcolor;
                color = statusColors.color;

                // Debug logging for marked for review questions
                if (status === STATUS.MARKED_FOR_REVIEW || status === STATUS.ANSWERED_MARKED_FOR_REVIEW) {
                  console.log(`🟣 Question ${index + 1} should be purple - Status: ${status}, Color: ${bgcolor}`);
                }
              }
              
              return (
                <Grid item key={index}>
                  <Tooltip title={`Question ${index + 1}${!hasBeenVisited ? ' (Not Visited)' : ''}`}>
                    <Button
                      variant="contained"
                      sx={{
                        minWidth: '36px',
                        height: '36px',
                        p: 0,
                        bgcolor,
                        color,
                        '&:hover': {
                          bgcolor: bgcolor,
                          opacity: 0.9
                        }
                      }}
                      onClick={() => navigateToQuestion(index)}
                    >
                      {index + 1}
                    </Button>
                  </Tooltip>
                </Grid>
              );
            }
          })}
        </Grid>
      </Paper>
    );
  };

  const renderExamSummary = () => {
    if (!examSubmitted) return null;

    // Calculate statistics
    let attemptedCount = 0;

    questions.forEach(question => {
      // Check if the question was attempted (has an answer)
      if (answers[question._id]) {
        attemptedCount++;
      }
    });

    const notAttemptedCount = questions.length - attemptedCount;

    console.log('Rendering exam summary with success message');

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 400, width: '100%', borderRadius: 2 }}>
          {/* Success icon */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#4caf50',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                fontSize: '40px',
                fontWeight: 'bold'
              }}
            >
              ✓
            </Box>
          </Box>

          <Typography variant="h5" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold', mb: 4 }}>
            Exam Submitted Successfully
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'left', mb: 2 }}>
            Exam Statistics:
          </Typography>

          <Box sx={{ textAlign: 'left', mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Questions attempted:</strong> {attemptedCount} out of {questions.length}
            </Typography>

            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Questions not attempted:</strong> {notAttemptedCount} out of {questions.length}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={props.onReturnToStart || (() => window.location.href = '/')}
          >
            RETURN TO DASHBOARD
          </Button>
        </Paper>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="contained" 
          color="primary" 
          sx={{ mt: 2 }}
          onClick={fetchQuestions}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  // If exam is submitted, show the success message with green tick
  if (examSubmitted) {
    return renderExamSummary();
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* If exam is submitted, show the success message */}
      {examSubmitted ? (
        renderExamSummary()
      ) : (
        <>
          {renderCurrentQuestion()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button 
              variant="outlined" 
              disabled={currentQuestionIndex === 0}
              onClick={goToPreviousQuestion}
              startIcon={<span>←</span>}
            >
              BACK
            </Button>
            
            {currentQuestionIndex === questions.length - 1 ? (
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSubmitExam}
              >
                SUBMIT
              </Button>
            ) : (
              <Button 
                variant="outlined"
                onClick={goToNextQuestion}
                endIcon={<span>→</span>}
              >
                NEXT
              </Button>
            )}
          </Box>
          
          {renderQuestionLegend()}
          {renderQuestionNavigation()}
        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      >
        <DialogTitle>Submit Exam</DialogTitle>
        <DialogContent>
          {/* Exam statistics before confirmation */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>Questions attempted:</strong> {Object.keys(answers).filter(qid => answers[qid]).length} out of {questions.length}
            </Typography>
            <Typography variant="body1">
              <strong>Questions remaining:</strong> {questions.length - Object.keys(answers).filter(qid => answers[qid]).length}
            </Typography>
            {typeof timeRemaining === 'number' && formatTime && (
              <Typography variant="body1">
                <strong>Time remaining:</strong> {formatTime(timeRemaining)}
              </Typography>
            )}
          </Box>
          <DialogContentText>
            Are you sure you want to submit your exam? You won't be able to change your answers after submission.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmSubmit} color="primary" autoFocus>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ExamQuestions; 